import {FC, ReactNode} from 'react'
import {
  Tooltip,
  type ITooltip,
  type PlacesType,
  type VariantType,
} from 'react-tooltip'
import classnames from 'classnames'

import {useTouchOnly} from 'hooks/useTouchOnly'

type TooltipBodyProps = {
  children: ReactNode
  testId?: string
  classes?: string
  noPadding?: boolean
  applyDefaultClasses?: boolean
  applyDefaultPadding?: boolean
}

type TooltipWrapperProps = {
  id: string
  variant?: VariantType
  place?: PlacesType
  hidden?: boolean
  delayShow?: number
  children: ReactNode
  className?: string
  noPadding?: boolean
  noArrow?: boolean
}

// Touch screens have no hover: a tap on the anchor opens the tooltip and a
// tap anywhere else, a scroll or Escape closes it.
export const TOUCH_TOOLTIP_PROPS: Partial<ITooltip> = {
  openOnClick: true,
  globalCloseEvents: {clickOutsideAnchor: true, scroll: true, escape: true},
}

// Used by DownloadButton; intended as a design tooltip within the design system.
const TooltipWrapper: FC<TooltipWrapperProps> = ({
  children,
  noPadding = false,
  className,
  ...rest
}) => {
  const isTouchOnly = useTouchOnly()
  return (
    <Tooltip
      opacity={1}
      {...(isTouchOnly ? TOUCH_TOOLTIP_PROPS : {})}
      {...rest}
      className={classnames(className, {
        '!p-0': noPadding,
      })}
    >
      {children}
    </Tooltip>
  )
}

const TooltipBody: FC<TooltipBodyProps> = ({
  children,
  testId = 'tooltip-body',
  classes = '',
  applyDefaultClasses = true,
  applyDefaultPadding = true,
  noPadding = false,
}) => (
  <div
    className={classnames(classes, {
      'bg-gray-800 text-xs abc-regular rounded-md overflow-hidden max-w-sm text-center shadow-lg':
        applyDefaultClasses,
      'p-0': noPadding,
      'px-2 py-1': applyDefaultPadding,
    })}
    data-cy={testId}
  >
    {children}
  </div>
)

export {TooltipWrapper, TooltipBody}
